import { rectAlignToPixels, rectExpand } from '../core/math';
import type { Point, Rect } from '../core/types';

/** Single-channel coverage for a shape, valid only inside `rect`. */
export interface ShapeRaster {
  rect: Rect;
  /** `rect.width * rect.height` bytes of 0..255 coverage. */
  data: Uint8Array;
}

/**
 * Selection shapes are rasterised by the 2D canvas rather than in GL: it gives
 * correct antialiasing and even-odd polygon filling for free, and only the shape
 * bounds are ever touched, so the cost is unrelated to document size.
 */
function rasterize(
  bounds: Rect,
  docWidth: number,
  docHeight: number,
  draw: (ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number) => void,
): ShapeRaster | null {
  const rect = rectAlignToPixels(rectExpand(bounds, 2), docWidth, docHeight);
  if (rect.width <= 0 || rect.height <= 0) return null;

  const canvas = document.createElement('canvas');
  canvas.width = rect.width;
  canvas.height = rect.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.clearRect(0, 0, rect.width, rect.height);
  ctx.fillStyle = '#fff';
  draw(ctx, rect.x, rect.y);

  const rgba = ctx.getImageData(0, 0, rect.width, rect.height).data;
  const data = new Uint8Array(rect.width * rect.height);
  for (let i = 0; i < data.length; i += 1) data[i] = rgba[i * 4 + 3];
  return { rect, data };
}

export function rasterizeRect(shape: Rect, docWidth: number, docHeight: number): ShapeRaster | null {
  return rasterize(shape, docWidth, docHeight, (ctx, ox, oy) => {
    ctx.fillRect(shape.x - ox, shape.y - oy, shape.width, shape.height);
  });
}

export function rasterizeEllipse(
  shape: Rect,
  docWidth: number,
  docHeight: number,
): ShapeRaster | null {
  return rasterize(shape, docWidth, docHeight, (ctx, ox, oy) => {
    const rx = shape.width / 2;
    const ry = shape.height / 2;
    ctx.beginPath();
    ctx.ellipse(shape.x - ox + rx, shape.y - oy + ry, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

export function rasterizePolygon(
  points: readonly Point[],
  docWidth: number,
  docHeight: number,
): ShapeRaster | null {
  if (points.length < 3) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const bounds: Rect = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

  return rasterize(bounds, docWidth, docHeight, (ctx, ox, oy) => {
    ctx.beginPath();
    ctx.moveTo(points[0].x - ox, points[0].y - oy);
    for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i].x - ox, points[i].y - oy);
    ctx.closePath();
    ctx.fill('evenodd');
  });
}
